/**
 * Cookie utilities for browser client
 * Handles reading/writing first-party cookies for identity persistence
 */

export interface CookieOptions {
  /** Cookie expiration in days (default: 365) */
  maxAgeDays?: number;
  /** Cookie domain (default: current domain) */
  domain?: string;
  /** Cookie path (default: '/') */
  path?: string;
  /** Secure flag (default: true in production) */
  secure?: boolean;
  /** SameSite attribute (default: 'Lax') */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

const DEFAULT_OPTIONS: CookieOptions = {
  maxAgeDays: 365,
  path: '/',
  sameSite: 'Lax',
};

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=').map((s) => s.trim());
    if (cookieName === name) {
      try {
        return decodeURIComponent(cookieValue);
      } catch {
        return cookieValue;
      }
    }
  }
  return undefined;
}

/**
 * Set a cookie with the given name and value
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const maxAge = (opts.maxAgeDays ?? 365) * 24 * 60 * 60;

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  cookieString += `; max-age=${maxAge}`;
  cookieString += `; path=${opts.path}`;

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }

  if (opts.secure ?? (typeof location !== 'undefined' && location.protocol === 'https:')) {
    cookieString += '; secure';
  }

  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;

  const opts = { ...DEFAULT_OPTIONS, ...options };

  let cookieString = `${name}=`;
  cookieString += '; max-age=0';
  cookieString += `; path=${opts.path}`;

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }

  document.cookie = cookieString;
}

/**
 * Cookie names used for identity tracking
 */
export const COOKIE_NAMES = {
  // Anonymous client ID (our own)
  ANONYMOUS_ID: '_oo_cid',

  // User data (email, name, external_id) for enhanced matching persistence
  USER_DATA: '_oo_user',

  // Meta (Facebook)
  FBP: '_fbp',
  FBC: '_fbc',

  // TikTok
  TTCLID: '_ttclid',
  TTP: '_ttp',

  // Google
  GCLID: '_gclid',
  GA_CLIENT_ID: '_ga',

  // Pinterest
  EPIK: '_epik',

  // Microsoft/Bing
  MSCLKID: '_msclkid',

  // Criteo
  CRITEO_ID: 'crto_mapped_user_id',
} as const;

/**
 * Read all identity cookies into an object.
 *
 * @param ga4MeasurementId When provided (e.g. "G-Q370D478VG"), ga_session_id
 *   is read ONLY from that stream's own `_ga_<suffix>` cookie. Without it the
 *   legacy first-match scan runs — which returns the WRONG property's
 *   session_id whenever a second Google tag has set its own `_ga_<MID>`
 *   cookie on the domain (2026-07-22 incident: 100% of TI + St. Frank GA4
 *   purchases landed Unassigned). Always configure this in production.
 */
export function readIdentityCookies(
  ga4MeasurementId?: string
): Record<string, string | undefined> {
  return {
    anonymous_id: getCookie(COOKIE_NAMES.ANONYMOUS_ID),
    fbp: getCookie(COOKIE_NAMES.FBP),
    fbc: getCookie(COOKIE_NAMES.FBC),
    ttclid: getCookie(COOKIE_NAMES.TTCLID),
    ttp: getCookie(COOKIE_NAMES.TTP),
    gclid: getCookie(COOKIE_NAMES.GCLID),
    ga_client_id: parseGaClientId(getCookie(COOKIE_NAMES.GA_CLIENT_ID)),
    ga_session_id: parseGaSessionId(getGa4SessionCookieValue(ga4MeasurementId)),
    epik: getCookie(COOKIE_NAMES.EPIK),
    msclkid: getCookie(COOKIE_NAMES.MSCLKID),
    criteo_id: getCookie(COOKIE_NAMES.CRITEO_ID),
  };
}

/**
 * Parse GA client ID from the _ga cookie
 * Format: GA1.2.123456789.1234567890 -> 123456789.1234567890
 */
function parseGaClientId(gaCookie: string | undefined): string | undefined {
  if (!gaCookie) return undefined;

  const parts = gaCookie.split('.');
  if (parts.length >= 4) {
    // Return the last two parts (client ID)
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }
  return gaCookie;
}

// Warn once per page load, not per capture call.
let warnedAmbiguousGa4Cookie = false;

/**
 * Find the GA4 session cookie (_ga_<MEASUREMENT_ID>) value.
 *
 * When `ga4MeasurementId` is provided, ONLY that stream's cookie is read
 * (`_ga_` + the ID without its `G-` prefix). If it's absent, we return
 * undefined rather than another stream's cookie: a truthful "no session on
 * our stream" keeps the MP event unattributed, while a foreign session_id
 * looks valid but can never join — that lie is exactly what made 100% of
 * TI + St. Frank purchases land Unassigned on 2026-07-22, when sale-launch
 * tooling put a second `_ga_<MID>` cookie on most buyers.
 *
 * Without a configured ID we fall back to the legacy first-match scan, and
 * warn (once) when the scan is ambiguous — i.e. more than one `_ga_<MID>`
 * cookie exists and we cannot know which one is ours.
 */
function getGa4SessionCookieValue(ga4MeasurementId?: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  if (ga4MeasurementId) {
    const suffix = ga4MeasurementId.replace(/^G-/, '');
    return getCookie(`_ga_${suffix}`);
  }

  const matches: string[] = [];
  let firstValue: string | undefined;
  for (const cookie of document.cookie.split(';')) {
    const [rawName, rawValue] = cookie.split('=').map((s) => s.trim());
    // GA4 session cookies are named _ga_<MID> where MID is uppercase letters + digits.
    // _ga (without trailing _<MID>) is the client_id cookie and parsed elsewhere.
    if (/^_ga_[A-Z0-9]+$/.test(rawName)) {
      matches.push(rawName);
      if (firstValue === undefined) {
        try {
          firstValue = decodeURIComponent(rawValue);
        } catch {
          firstValue = rawValue;
        }
      }
    }
  }
  if (matches.length > 1 && !warnedAmbiguousGa4Cookie) {
    warnedAmbiguousGa4Cookie = true;
    // eslint-disable-next-line no-console
    console.warn(
      `[UES] ${matches.length} _ga_<MID> cookies found (${matches.join(', ')}) and no ` +
        'ga4MeasurementId configured — first-match may capture the WRONG property’s ' +
        'session_id and purchases will land Unassigned in GA4. Set ga4MeasurementId in ' +
        'the UES client config.'
    );
  }
  return firstValue;
}

/**
 * Extract the GA4 session_id from a _ga_<MID> cookie value.
 *
 * GA4 has shipped at least three cookie formats in the wild:
 *   - Classic:    GS1.1.<session_id>.<n>.0.<t>.0.0.0
 *   - Consent v2: GS1.1.s<session_id>$o<n>$g<n>$t<t>$j<n>$l<n>$h<n>
 *   - GS2.1:      GS2.1.s<session_id>$o<n>$g<n>$t<t>$j<n>$l<n>$h<n>
 *
 * Every prior structural parser (positional-index, prefix-checked,
 * regex-shaped) has been broken by a subsequent GA4 revision. See OWN-2069.
 *
 * The one thing that hasn't changed: session_id is always the first long
 * numeric run in the cookie — a Unix timestamp (10 digits today, growing to
 * 11 in 2286). We match on that invariant instead of the surrounding shape.
 *
 * Trade-off: this returns any 10+-digit number that appears before another,
 * even if a future GA4 format prefixes the payload with something numeric.
 * Public GA4 cookie documentation shows no such lead-in field on any known
 * variant. If one appears later, this returns the wrong session_id, but the
 * failure mode is soft — GA4's downstream dedup by transaction_id + relative
 * timestamp still holds, and we'll see it in Datadog identity coverage.
 * Preferred over the brittleness we've already been bitten by twice.
 */
function parseGaSessionId(gaSessionCookie: string | undefined): string | undefined {
  if (!gaSessionCookie) return undefined;
  const match = gaSessionCookie.match(/\d{10,}/);
  return match ? match[0] : undefined;
}

/**
 * Generate a UUID v4 for anonymous ID
 */
export function generateAnonymousId(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create anonymous ID
 */
export function getOrCreateAnonymousId(options?: CookieOptions): string {
  let anonymousId = getCookie(COOKIE_NAMES.ANONYMOUS_ID);

  if (!anonymousId) {
    anonymousId = generateAnonymousId();
    setCookie(COOKIE_NAMES.ANONYMOUS_ID, anonymousId, {
      ...options,
      maxAgeDays: 730, // 2 years
    });
  }

  return anonymousId;
}
